import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { getFileExtension } from '@ftr/contracts/type/core'
import { ButtonDisplayType } from '@ftr/foundation'
import { LoggingService } from '@ftr/ui-observability'
import { BehaviorSubject, Observable, catchError, map, of, switchMap, tap } from 'rxjs'
import { FileOrder, FileUpload, FileUploadError, UploadStates } from './file-upload'
import { FileUploadService } from './file-upload.service'
import { VirusScanningService } from './virus-scanning/virus-scanning.service'

@Component({
  selector: 'ftr-file-upload-item',
  templateUrl: './file-upload-item.component.html',
  styleUrls: ['./file-upload-item.component.css'],
})
export class FileUploadItemComponent<FileUploadPropertiesType, FileUploadResponseType> implements OnInit {
  /**
   * The file to upload using this component.
   */
  @Input() fileToUpload: FileUpload<FileUploadPropertiesType, FileUploadResponseType>

  /**
   * The size limit that cannot be exceeded.
   */
  @Input() sizeLimit: number

  /**
   * Option for ftr-file-selector: pre-set file browser popup to filter by these file extensions.
   * Also ignores incorrect file types dropped with ftr-drop-overlay.
   */
  @Input() acceptableFileExtensions: string[]

  /**
   * Indicates the order of this file in the list.
   */
  @Input() fileOrder: FileOrder

  /**
   * An implementation of the FileUploadService that should be used to upload files with.
   */
  @Input() fileUploadService: FileUploadService<FileUploadPropertiesType, FileUploadResponseType>

  @Output() uploadStatusChange = new EventEmitter<FileUpload<FileUploadPropertiesType, FileUploadResponseType>>()

  readonly buttonDisplayType = ButtonDisplayType
  readonly uploadStates = UploadStates

  fileUpload: Observable<FileUpload<FileUploadPropertiesType, FileUploadResponseType>>

  private refreshUpload = new BehaviorSubject<void>(undefined)
  private refreshScan = new BehaviorSubject<void>(undefined)
  private cancelUploadSubject = new BehaviorSubject<
    FileUpload<FileUploadPropertiesType, FileUploadResponseType> | undefined
  >(undefined)
  private isNewFile = true

  constructor(
    private readonly virusScanningService: VirusScanningService<FileUploadPropertiesType, FileUploadResponseType>,
    private readonly loggingService: LoggingService,
  ) {}

  ngOnInit(): void {
    this.fileUpload = this.upload(this.fileToUpload)
  }

  retry(upload: FileUpload<FileUploadPropertiesType, FileUploadResponseType>): void {
    if (this.isScanningFailure(upload)) {
      this.refreshScan.next()
    } else {
      this.refreshUpload.next()
    }
  }

  cancelUpload(upload: FileUpload<FileUploadPropertiesType, FileUploadResponseType>): void {
    this.cancelUploadSubject.next(upload)
  }

  isOverSizeLimit(upload: FileUpload<FileUploadPropertiesType, FileUploadResponseType>): boolean {
    return !!(this.sizeLimit && upload.file.size > this.sizeLimit)
  }

  isFileExtensionAccepted(upload: FileUpload<FileUploadPropertiesType, FileUploadResponseType>): boolean {
    const extension = getFileExtension(upload.file.name)
    if (!extension) {
      return false
    }
    return this.acceptableFileExtensions.includes(extension)
  }

  isUploading(upload: FileUpload<FileUploadPropertiesType, FileUploadResponseType>): boolean {
    return (
      upload.status === UploadStates.Uploading ||
      upload.status === UploadStates.Initializing ||
      upload.status === UploadStates.Finalizing
    )
  }

  isScanning(upload: FileUpload<FileUploadPropertiesType, FileUploadResponseType>): boolean {
    return upload.status === UploadStates.Scanning
  }

  isDeleting(upload: FileUpload<FileUploadPropertiesType, FileUploadResponseType>): boolean {
    return upload.status === UploadStates.Deleting
  }

  isRetryEnabled(upload: FileUpload<FileUploadPropertiesType, FileUploadResponseType>): boolean {
    return !(this.isOverSizeLimit(upload) || !this.isFileExtensionAccepted(upload) || this.isFileInfected(upload))
  }

  private isFileInfected(upload: FileUpload<FileUploadPropertiesType, FileUploadResponseType>): boolean {
    return upload.error === FileUploadError.VirusDetected
  }

  private isScanningFailure(upload: FileUpload<FileUploadPropertiesType, FileUploadResponseType>): boolean {
    return (
      upload.error === FileUploadError.ScanFailedToStart ||
      upload.error === FileUploadError.ScanTimedOut ||
      upload.error === FileUploadError.ScanFailed
    )
  }

  private upload(
    newUpload: FileUpload<FileUploadPropertiesType, FileUploadResponseType>,
  ): Observable<FileUpload<FileUploadPropertiesType, FileUploadResponseType>> {
    if (newUpload.status === UploadStates.Success || newUpload.status === UploadStates.Failure) {
      // Existing file, enable cancel and return
      this.isNewFile = false
      return this.enableCancel(of(newUpload))
    }

    if (!this.isFileExtensionAccepted(newUpload)) {
      return this.enableCancel(
        of({
          ...newUpload,
          status: UploadStates.Failure,
          error: FileUploadError.FileTypeNotAccepted,
        }),
      )
    }

    return this.enableCancel(
      this.refreshUpload.pipe(
        switchMap(() =>
          this.fileUploadService.upload(newUpload, this.sizeLimit).pipe(
            catchError(() => {
              this.loggingService.warn({
                message: FileUploadError.FileUploadFailed,
                uploadId: newUpload.id,
                fileId: newUpload.fileId,
              })

              return of({
                ...newUpload,
                status: UploadStates.Failure,
                error: FileUploadError.FileUploadFailed,
              })
            }),
            switchMap(upload => {
              if (upload.status === UploadStates.Success && this.fileUploadService.addVirusScanning) {
                return this.scan(upload)
              }

              this.uploadStatusChange.emit(upload)
              return of(upload)
            }),
          ),
        ),
      ),
    )
  }

  private cancel(
    upload: FileUpload<FileUploadPropertiesType, FileUploadResponseType>,
  ): Observable<FileUpload<FileUploadPropertiesType, FileUploadResponseType>> {
    // Only delete new files from S3 and DB if their initialization was successful
    if (
      this.isNewFile &&
      upload.fileId &&
      upload.status !== UploadStates.Initializing &&
      (upload.status !== UploadStates.Failure || !this.isFileInfected(upload))
    ) {
      return this.fileUploadService.deleteFile(upload).pipe(
        map(remote => {
          if (remote.isSuccess() || remote.isFailure()) {
            // Swallow the error and cancel the file if it can't be deleted from s3
            upload.status = UploadStates.Canceled
          } else {
            upload.status = UploadStates.Deleting
          }
          this.uploadStatusChange.emit(upload)
          return upload
        }),
      )
    } else {
      upload.status = UploadStates.Canceled
      this.uploadStatusChange.emit(upload)
      return of(upload)
    }
  }

  private scan(
    upload: FileUpload<FileUploadPropertiesType, FileUploadResponseType>,
  ): Observable<FileUpload<FileUploadPropertiesType, FileUploadResponseType>> {
    return this.refreshScan.pipe(
      switchMap(() => {
        return this.virusScanningService.initializeScan(upload).pipe(
          catchError(erroredUpload => {
            this.loggingService.warn({
              message: erroredUpload.error,
              uploadId: erroredUpload.id,
              fileId: erroredUpload.fileId,
            })
            return of(erroredUpload)
          }),
        )
      }),
      switchMap(uploaded =>
        this.virusScanningService
          .checkScanningProgress(uploaded)
          .pipe(tap(scanning => this.uploadStatusChange.emit(scanning))),
      ),
    )
  }

  /**
   * Allows the user to cancel this upload
   */
  private enableCancel(
    upload: Observable<FileUpload<FileUploadPropertiesType, FileUploadResponseType>>,
  ): Observable<FileUpload<FileUploadPropertiesType, FileUploadResponseType>> {
    return this.cancelUploadSubject.pipe(switchMap(cancel => (cancel ? this.cancel(cancel) : upload)))
  }
}
